/**
 * Per-case state reset.
 *
 * CaseViewer mounts fresh per study (key={studyId}), so most state is
 * initialized via atom defaults — but Jotai atoms are application-global
 * and don't auto-reset across cases. This atom collapses the prior 13
 * separate `useSetAtom` calls in CaseViewer into a single write that
 * resets every per-case atom in lockstep.
 *
 * Kept in its own file to avoid pulling segmentation imports into
 * `selectionAtoms.ts` and creating a circular-import risk.
 */

import { atom } from "jotai";

import {
  activeGroupIdAtom,
  currentSliceAtom,
  loadedSeriesAtom,
  pendingReportInsertAtom,
  selectedFindingConditionAtom,
  selectedSeriesIdAtom,
  seriesErrorAtom,
  seriesLoadingStateAtom,
  studyIsLoadingAtom,
  studyMetadataAtom,
  targetSeriesIdAtom,
  totalSlicesAtom,
} from "./selectionAtoms";
import {
  activeSegmentIndexAtom,
  annotationStartTsAtom,
  derivedMeasurementsAtom,
  isDrawingAtom,
  nnClickMarkersAtom,
  nnInteractiveLoadingAtom,
  nnSegmentationResultAtom,
  nnSessionActiveAtom,
  savedAnnotationsAtom,
  segmentationVolumeIdAtom,
  selectedAnnotationIdAtom,
} from "./segmentationAtoms";
import { segmentationMarksAtom } from "./segmentationMarksAtom";
import { aiJumpTargetAtom } from "./positionAtoms";
import { selectedStoneIdAtom, stoneAttrEditsAtom, stoneVerdictsAtom } from "./stoneReviewAtoms";

/**
 * Write-only atom: resets all per-case atoms back to their defaults.
 * Use via `useSetAtom(resetCaseAtomsAtom)` and call once on mount.
 */
export const resetCaseAtomsAtom = atom(null, (_get, set) => {
  // Selection. studyMetadataAtom is the SSoT — clearing it cascades through
  // the derived instanceToSliceMap / instanceSliceInfoMap atoms.
  set(currentSliceAtom, 0);
  set(totalSlicesAtom, 0);
  set(targetSeriesIdAtom, null);
  set(studyMetadataAtom, null);
  set(activeGroupIdAtom, null);
  set(selectedFindingConditionAtom, null);
  // sliceNavRequest: now a bus channel — no atom to reset. Bus events are
  // fire-and-forget; the new case's ProtocolViewport subscribes fresh on mount.
  // pendingReportInsertAtom: stale finding text would land on the new case's editor.
  set(pendingReportInsertAtom, null);
  // Stale AI-jump target from the prior case would fire on the new case's
  // first volume-ready event and yank every viewport to the prior IPP.
  set(aiJumpTargetAtom, null);
  // Series loader state — previously useState in CaseViewer, reset by
  // remount. Now atom-backed, so the case switch clears them explicitly.
  set(selectedSeriesIdAtom, null);
  set(loadedSeriesAtom, []);
  set(studyIsLoadingAtom, false);
  set(seriesLoadingStateAtom, "idle");
  set(seriesErrorAtom, null);
  // Segmentation
  set(savedAnnotationsAtom, []);
  set(activeSegmentIndexAtom, 1);
  set(isDrawingAtom, false);
  set(annotationStartTsAtom, null);
  set(derivedMeasurementsAtom, null);
  set(segmentationVolumeIdAtom, null);
  set(selectedAnnotationIdAtom, null);
  // nnInteractive overlay state. Without explicit resets these survive case
  // navigation and surface as stuck spinners, leftover click markers, or a
  // stale cross-plane sync tick on the scrollbar of the new case. (#396)
  set(nnSessionActiveAtom, false);
  set(nnClickMarkersAtom, []);
  set(nnInteractiveLoadingAtom, { isLoading: false, viewportId: null });
  set(nnSegmentationResultAtom, null);
  // nnCrossViewportSync: now a bus channel — no atom to reset. Bus events are
  // fire-and-forget; the new case's subscribers re-bind on mount.
  // Stone review. Verdicts + attr edits are atomWithStorage keyed by bare
  // stoneId — without an explicit per-case reset, prior approvals bleed into
  // the next study's rail (and its report chip). selectedStoneId is plain
  // in-memory but would point at a stone that isn't in the new case.
  set(stoneVerdictsAtom, {});
  set(stoneAttrEditsAtom, {});
  set(selectedStoneIdAtom, null);
  // Segmentation marks are per-study: leaving them set paints the previous study's
  // ticks on the new case's scrollbar until the resolver re-runs.
  set(segmentationMarksAtom, null);
});
